import { Icon, IconButton, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import { useContext, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Notification } from '@hero/atoms';
import { formatCurrency } from '@hero/utils';

import { EditModalContext } from '../ContactsEditModal/context';
import { NavigationSecondaryContent } from '../ContactsEditModal/NavigationSecondaryContent';
import { ReviewAndSubmit } from '../Recognition';

import { FLOW_STEPS } from './constant';
import * as S from './style';

function GiftReviewAndSubmit({
  updateProducts,
  onRequestClose,
  selectedProducts,
  setModalObject,
  setCurrentFlow,
  isCCPayment,
  isLiveFeature
}) {
  const { t } = useTranslation();
  const newEstimatedCharge = useContext(EditModalContext);

  useEffect(() => {
    setModalObject({
      title: (
        <S.ContactHeader>
          <IconButton
            color="neutral"
            onClick={() => {
              setCurrentFlow(FLOW_STEPS.productList);
            }}
          >
            <Icon name="left-chevron" />
          </IconButton>
          <VerticalDivider $height="24px" $margin="0 16px 0 8px" />
          {t('reviewAndConfirm')}
        </S.ContactHeader>
      ),
      primaryButtonProps: {
        children: t(isCCPayment ? 'payAndSubmit' : 'submit'),
        onClick: async () => {
          await updateProducts(selectedProducts);
          onRequestClose();
        }
      },
      components: !isLiveFeature
        ? {
            SecondaryContent: NavigationSecondaryContent
          }
        : {}
    });
  }, []);

  const table = useMemo(() => {
    const newTable = [
      {
        key: t('highestPricedItemNow'),
        value: formatCurrency(newEstimatedCharge?.new?.baseProductPrice),
        isActive: true
      },
      {
        key: t('highestPricedItemPrevious'),
        value: formatCurrency(newEstimatedCharge?.current?.baseProductPrice)
      }
    ];

    if (newEstimatedCharge) {
      const minEstimatedCharge = newEstimatedCharge?.new?.serviceCharges?.min;
      const maxEstimatedCharge = newEstimatedCharge?.new?.serviceCharges?.max;
      const shouldShowEstimatedChargeRange = minEstimatedCharge === 0 && maxEstimatedCharge !== 0;

      newTable.push({
        key: (
          <Typography as="div" variant="body2" style={{ display: 'flex', gap: 4 }}>
            {t('totalEstimatedSpend')}
            <Typography variant="body2" color="fg" emphasis="weak">
              ({t('perRecipient').toLocaleLowerCase()})
            </Typography>
          </Typography>
        ),
        type: 'accordion',
        value: formatCurrency(newEstimatedCharge?.difference?.total),
        table: [
          {
            key: t('items'),
            // helpText: `(${t('costSummarySubTitle2', {
            //   recipientCount: newEstimatedCharge?.new?.recipientCount,
            //   amount:
            //     newEstimatedCharge?.new?.baseProductPrice -
            //     newEstimatedCharge?.current?.baseProductPrice
            // })})`,
            value: formatCurrency(newEstimatedCharge?.difference?.merchandise)
          },
          {
            key: (
              <>
                {t('estimatedServiceAndShippingFees')}
                {shouldShowEstimatedChargeRange && (
                  <Typography variant="caption1" color="fg" emphasis="weak">
                    {t('serviceChargeMessageForFreeShip')}
                  </Typography>
                )}
              </>
            ),
            value: shouldShowEstimatedChargeRange ? (
              <>
                {formatCurrency(minEstimatedCharge)} - {formatCurrency(maxEstimatedCharge)}
              </>
            ) : (
              formatCurrency(newEstimatedCharge?.difference?.serviceCharge)
            )
          },
          {
            key: t('estimatedTax'),
            value: formatCurrency(newEstimatedCharge?.difference?.estimatedTax)
          }
        ]
      });
    }

    return newTable.filter(n => !n.isHidden);
  }, [newEstimatedCharge]);

  return (
    <>
      <Notification
        isClosable
        style={{ marginBottom: 16 }}
        status="info"
        message={<Trans i18nKey="introducingAHigherPricedItem" components={{ b: <b /> }} />}
      />
      <ReviewAndSubmit
        icon="gift-box"
        desc={t('reviewAndConfirmDescription')}
        table={table.filter(t => !!t)}
      />
    </>
  );
}

export { GiftReviewAndSubmit };
